import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  DestroyRef,
  OnDestroy,
  OnInit,
  Renderer2,
  inject,
  signal,
  viewChild,
} from '@angular/core'
import {takeUntilDestroyed} from '@angular/core/rxjs-interop'
import {
  ActivatedRoute,
  ActivatedRouteSnapshot,
  ActivationEnd,
  NavigationEnd,
  NavigationStart,
  Router,
} from '@angular/router'
import {Capacitor, SystemBarType, SystemBars, SystemBarsStyle} from '@capacitor/core'
import {Keyboard} from '@capacitor/keyboard'
import {ScreenOrientation} from '@capacitor/screen-orientation'
import {SplashScreen} from '@capacitor/splash-screen'
import {ChatReactivityService} from '@features/echo/services/chat-reactivity.service'
import {IonApp, IonButton, IonRouterOutlet, ModalController, NavController, Platform} from '@ionic/angular/standalone'
import {filter, take} from 'rxjs/operators'
import {MenuComponent} from '@common/components/shared/menu/menu.component'
import {PlatformType, PreferencesStorageIds} from '@common/global-constants'
import {ToastUtil} from '@common/utils/toast.util'
import {environment} from '@environments/environment'
import {PORTRAIT} from '@models/constants/orientations.constant'
import {NavControllerDirection} from '@models/enums/nav-controller'
import {AuthService} from '@service/auth/auth.service'
import {BackgroundTaskService} from '@service/background-task.service'
import {HttpCancelService} from '@service/config/http-cancel.service'
import {GpsService} from '@service/gps.service'
import {LocalNotificationService} from '@service/local-notification.service'
import {LoggingService} from '@service/logging.service'
import {MultimediaService} from '@service/multimedia.service'
import {NetworkService} from '@service/network.service'
import {PermissionsService} from '@service/permissions/permissions.service'
import {PlatformService} from '@service/platform.service'
import {ScannerService} from '@service/scanner.service'
import {PreferencesApiService} from '@service/storage/preferences-api.service'
import {StorageApiService} from '@service/storage/storage-api.service'
import {RestSyncService} from '@service/sync/rest-sync.service'
import {MonitoringRouteTracker} from '@service/monitoring/monitoring-route-tracker.service'
import {MonitoringService} from '@service/monitoring/monitoring.service'
import {PageNavigationService} from '@service/ui/page-navigation.service'
import {SessionService} from '@uve-solutions/api-angular-client'
import {KpiConnectorService} from '@uve-solutions/shared-kpis'
import {ConnectivityNotificationService} from '@service/connectivity-notification.service'
import {LoadingComponent} from './common/components/loading/loading.component'
import {SplashComponent} from './common/components/splash/splash.component'
import {ToastNotificationContainerDirective} from './common/components/toast-notification/toast-notification.directive'
import {UveTranslatePipe} from './pipes/uve-translate.pipe'

@Component({
  selector: 'app-root',
  imports: [
    IonApp,
    IonRouterOutlet,
    IonButton,
    MenuComponent,
    SplashComponent,
    LoadingComponent,
    ToastNotificationContainerDirective,
    UveTranslatePipe,
  ],
  templateUrl: './app.component.html',
  styleUrl: './app.component.scss',
})
export class AppComponent implements OnInit, AfterViewInit, OnDestroy {
  readonly menu = viewChild(MenuComponent)

  version = signal('0.0.0')
  hideApp = signal(false)
  isClicked = false
  currentUrl = signal('')
  swipeGesture = signal(true)

  public readonly authService = inject(AuthService)
  public readonly modalController = inject(ModalController)
  public readonly sessionService = inject(SessionService)
  public readonly scannerService = inject(ScannerService)
  public readonly networkService = inject(NetworkService)
  public readonly restSyncService = inject(RestSyncService)

  private readonly destroyRef$ = inject(DestroyRef)
  private readonly platform = inject(Platform)
  private readonly permissions = inject(PermissionsService)
  private readonly kpiConnectorService = inject(KpiConnectorService)
  private readonly storageApiService = inject(StorageApiService)
  private readonly preferencesApiService = inject(PreferencesApiService)
  private readonly renderer = inject(Renderer2)
  private readonly router = inject(Router)
  private readonly activatedRoute = inject(ActivatedRoute)
  private readonly platformService = inject(PlatformService)
  private readonly multimediaService = inject(MultimediaService)
  private readonly gpsService = inject(GpsService)
  private readonly cdr = inject(ChangeDetectorRef)
  private readonly navController = inject(NavController)
  private readonly pageNavigationService = inject(PageNavigationService)
  private readonly httpCancelService = inject(HttpCancelService)
  private readonly backgroundTaskService = inject(BackgroundTaskService)
  private readonly loggingService = inject(LoggingService)
  private readonly localNotificationService = inject(LocalNotificationService)
  private readonly chatReactivityService = inject(ChatReactivityService)
  private readonly monitoringRouteTracker = inject(MonitoringRouteTracker)
  private readonly monitoring = inject(MonitoringService)
  private readonly connectivityNotificationService = inject(ConnectivityNotificationService)

  constructor() {
    this.backgroundTaskService.initTasks()
    this.permissions.init()
    this.chatReactivityService.init()
  }

  ngOnInit(): void {
    this.initializeApp()
  }

  initializeApp = async (): Promise<void> => {
    const _env = environment as Partial<{version?: string; build?: string}>
    const buildVersion = _env.build ? ` (${_env.build})` : ''
    const fullVersion = `${_env.version}${buildVersion}`

    this.version.set(fullVersion)

    this.initRouterListener()
    this.monitoringRouteTracker.init()
    this.emitAppOpen()

    this.connectivityNotificationService.init()

    this.setupKeyboard()

    await this.storageApiService.init()
    await this.gpsService.initService()
    await this.multimediaService.checkAndRequestCameraPermission()
    await this.localNotificationService.requestPermission()

    this.platform.ready().then(() => {
      this.setOrientation()
      this.actionsWhenRouterNavigationEnd()

      SplashScreen.hide()

      // Ajuste para que los iconos del status-bar se vean en blanco correctamente
      if (this.platform.is(PlatformType.CAPACITOR)) {
        SystemBars.setStyle({style: SystemBarsStyle.Dark, bar: SystemBarType.StatusBar})
      }

      this.renderer.addClass(
        document.querySelector('body'),
        this.platform.is(PlatformType.CAPACITOR) ? 'is-capacitor-device' : 'is-web-device'
      )
    })

    this.overrideIonicAttachShadow()

    this.kpiConnectorService.customClick = true
    this.scannerService.hideApp.subscribe(response => this.hideApp.set(response))

    // Reset state values user when logout
    this.authService.notifierUserAuthenticated$.pipe(takeUntilDestroyed(this.destroyRef$)).subscribe(() => {
      this.cdr.detectChanges()
    })
  }

  ngAfterViewInit(): void {
    this.platformService.checkIsMobile()
  }

  ngOnDestroy(): void {
    if (this.platform.is(PlatformType.CAPACITOR)) {
      Keyboard.removeAllListeners()
    }
    this.authService.unsuscribeEvents()
    this.preferencesApiService.set(PreferencesStorageIds.LAST_SYNC, new Date().toUTCString())
  }

  private readonly emitAppOpen = (): void => {
    this.authService.notifierUserAuthenticated$
      .pipe(filter(Boolean), take(1), takeUntilDestroyed(this.destroyRef$))
      .subscribe(() => {
        this.monitoring.track('APP_OPEN', {
          app_version: environment.version,
          platform: Capacitor.getPlatform(),
          locale: navigator.language,
        })
      })
  }

  private readonly setOrientation = (): void => {
    if (
      this.platform.is(PlatformType.CAPACITOR) &&
      this.platform.is(PlatformType.MOBILE) &&
      !this.platform.is(PlatformType.TABLET)
    ) {
      ScreenOrientation.lock({orientation: PORTRAIT})
    }
  }

  private readonly overrideIonicAttachShadow = (): void => {
    // FIXES ionic keybaord opens unexpectedly bug
    // Similar to -> https://github.com/ionic-team/ionic-framework/issues/21509
    const defaultAttachShadow = HTMLElement.prototype.attachShadow
    HTMLElement.prototype.attachShadow = function (options) {
      if (this.tagName === 'ION-ITEM') {
        return defaultAttachShadow.bind(this)({
          ...options,
          delegatesFocus: false,
        })
      } else {
        return defaultAttachShadow.bind(this)(options)
      }
    }
  }

  private readonly getActivatedRouteSnapshot = (snapshot: ActivatedRouteSnapshot): ActivatedRouteSnapshot => {
    if (snapshot.firstChild) {
      return this.getActivatedRouteSnapshot(snapshot.firstChild)
    }

    return snapshot
  }

  private readonly setupKeyboard = (): void => {
    const el = document.querySelector('body')

    if (this.platform.is(PlatformType.CAPACITOR)) {
      Keyboard.addListener('keyboardWillShow', () => {
        el.classList.add('keyboard-is-open')
      })

      Keyboard.addListener('keyboardWillHide', () => {
        el.classList.remove('keyboard-is-open')
      })
    }
  }

  private readonly actionsWhenRouterNavigationEnd = (): void => {
    const {data = {}} = this.getActivatedRouteSnapshot(this.activatedRoute.snapshot)

    this.swipeGesture.set(data?.menuSwipeGestureActive ?? false)
  }

  private readonly initRouterListener = (): void => {
    this.router.events
      .pipe(
        takeUntilDestroyed(this.destroyRef$),
        filter(event => event instanceof NavigationEnd || event instanceof NavigationStart)
      )
      .subscribe(event => {
        if (event instanceof NavigationStart) {
          const isForwardNavigation = this.navController['direction'] !== NavControllerDirection.BACK

          this.pageNavigationService.setIsForwardNavigation(isForwardNavigation)

          const previousUrl = this.router.currentNavigation()?.previousNavigation?.finalUrl?.toString()

          this.pageNavigationService.setPreviousNavigationUrl(previousUrl)
        }

        if (event instanceof NavigationEnd) {
          this.currentUrl.set(event.url)

          this.actionsWhenRouterNavigationEnd()

          ToastUtil.clearAll()
        }

        if (event instanceof ActivationEnd) {
          this.httpCancelService.cancelPendingRequests()
        }
      })
  }
}
